/* eslint-disable */
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Spin, Collapse, Popover, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@app/store/store';
import { BaseForm } from '../common/forms/BaseForm/BaseForm';
import { Loading } from '../common/Loading/Loading';
import * as Auth from '@app/components/layouts/AuthLayout/AuthLayout.styles';
import { InfoCircleOutlined, PlusOutlined, MinusOutlined } from '@ant-design/icons';

import { GetCampaignProductsWithPackages } from '@app/api/voucher.api';
import { CampaignPackageDto, ProductsWithPackages, ProductWithPackages } from '@app/interfaces/voucher';
import {
  PackageRow,
  PackageNameWrap,
  PackageName,
  QtyControl,
  QtyButton,
  QtyValue,
  HeaderRight,
  HeaderCount,
  SectionNote,
  InfoButton,
  PopoverContent,
  PopoverDesc,
  PopoverTitle,
  PackageActionWrap,
  PanelHeader,
  PanelTitle,
  PackageNote,
} from './PackageForm.styles';
import { setProductsWithPackages, setSelectedPackagesByProduct } from '@app/store/slices/voucherSlice';
import { useLanguage } from '@app/hooks/useLanguage';

const { Panel } = Collapse;

type Id = string;

type SelectedPackageItem = CampaignPackageDto & {
  Id: string;
  Name: string;
  Description?: string;
  ord?: number;
  ProductId: string;
  Quantity: number;
};

type PicksByProduct = Record<Id, SelectedPackageItem[]>;

function normId(x: any): string {
  return String(x?.Id ?? x?.id ?? '');
}

function normStr(x: string, fallback = ''): string {
  return String(x ?? fallback);
}

function normOrd(x: any): number {
  return Number(x?.ord ?? x?.Ord ?? 0);
}

function normalizeProducts(data: any): ProductWithPackages[] {
  const rawProducts = data?.Products ?? [];
  return (rawProducts || []).map((p: any) => {
    const productId = normId(p);

    const pkgs = (p?.Packages ?? [])
      .map((k: any) => ({
        ...k,
        Id: normId(k),
        Name: k?.Name ?? k?.name ?? '',
        Description: k?.Description ?? k?.description ?? '',
        ord: normOrd(k),
        ProductId: productId,
      }))
      .sort((a: any, b: any) => (a?.ord ?? 0) - (b?.ord ?? 0));

    return {
      ...p,
      Id: productId,
      Name: p?.Name ?? p?.name ?? '',
      MaxSelection: Number(p?.MaxSelection ?? p?.maxSelection ?? 0),
      Packages: pkgs,
    };
  });
}

function buildInitialPicks(
  normalizedProducts: ProductWithPackages[],
  savedByProduct?: Record<string, SelectedPackageItem[]>,
): PicksByProduct {
  const result: PicksByProduct = {};

  normalizedProducts.forEach((product: ProductWithPackages) => {
    const savedMap = new Map<string, number>();

    (savedByProduct?.[String(product.Id)] || []).forEach((x: any) => {
      savedMap.set(String(x?.Id ?? x?.id), Number(x?.Quantity || 0));
    });

    result[String(product.Id)] = ((product.Packages || []) as any[]).map((pkg: any) => ({
      ...pkg,
      Id: normId(pkg),
      Name: pkg?.Name ?? pkg?.name ?? '',
      Description: pkg?.Description ?? pkg?.description ?? '',
      ord: normOrd(pkg),
      ProductId: String(product.Id),
      Quantity: savedMap.get(normId(pkg)) || 0,
    }));
  });

  return result;
}

function getTotalQtyOfProduct(items: SelectedPackageItem[] = []) {
  return (items || []).reduce((sum, x) => sum + Number(x?.Quantity || 0), 0);
}

const PackageForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { language } = useLanguage();
  const voucher = useSelector((state: RootState) => state.voucherSlice.voucher);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;

  const savedByProduct = useSelector(
    (state: RootState) => (state.voucherSlice as any).selectedPackagesByProduct as PicksByProduct | undefined,
  );

  const [isLoading, setIsLoading] = useState(true);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [error, setError] = useState<string>('');
  const [products, setProducts] = useState<ProductWithPackages[]>([]);
  const [picksByProduct, setPicksByProduct] = useState<PicksByProduct>({});
  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  useEffect(() => {
    if (voucher) setIsLoading(false);
    else navigate('../');
  }, [voucher, navigate]);

  useEffect(() => {
    if (!voucher) return;

    const campaignId = String((voucher as any).CampaignId ?? '');

    if (!campaignId) {
      setError(t('Không tìm thấy campaignId từ voucher.'));
      return;
    }

    let mounted = true;
    setLoadingPackages(true);
    setError('');

    GetCampaignProductsWithPackages(campaignId, language)
      .then((payload: ProductsWithPackages | any) => {
        if (!mounted) return;

        const normalizedProducts = normalizeProducts(payload).sort(
          (a: ProductWithPackages, b: ProductWithPackages) => (a?.OrderNo ?? 0) - (b?.OrderNo ?? 0),
        );

        setProducts(normalizedProducts);

        dispatch(
          setProductsWithPackages({
            ...(payload || {}),
            Products: normalizedProducts,
          }),
        );

        const init = buildInitialPicks(normalizedProducts, savedByProduct);
        setPicksByProduct(init);
        setActiveKeys(normalizedProducts[0] ? [String(normalizedProducts[0].Id)] : []);
      })
      .catch((e: any) => {
        if (!mounted) return;
        setError(e?.message || t('Không tải được danh mục gói.'));
      })
      .finally(() => {
        if (!mounted) return;
        setLoadingPackages(false);
      });

    return () => {
      mounted = false;
    };
  }, [voucher, savedByProduct, language]);

  const handleIncrease = (productId: string, packageId: string) => {
    setPicksByProduct((prev) => {
      const current = prev[productId] || [];
      const product = products.find((x) => String(x.Id) === String(productId));
      if (!product) return prev;

      const maxSelection = Number((product as any).MaxSelection ?? 0);
      const totalQty = getTotalQtyOfProduct(current);

      if (maxSelection > 0 && totalQty >= maxSelection) return prev;

      return {
        ...prev,
        [productId]: current.map((x) =>
          String(x.Id) === String(packageId) ? { ...x, Quantity: Number(x.Quantity || 0) + 1 } : x,
        ),
      };
    });
  };

  const handleDecrease = (productId: string, packageId: string) => {
    setPicksByProduct((prev) => {
      const current = prev[productId] || [];

      return {
        ...prev,
        [productId]: current.map((x) =>
          String(x.Id) === String(packageId) ? { ...x, Quantity: Math.max(Number(x.Quantity || 0) - 1, 0) } : x,
        ),
      };
    });
  };

  const validateSelection = (): { ok: boolean; message?: string; focusProductId?: string } => {
    for (const p of products) {
      const pickedItems = picksByProduct[String(p.Id)] || [];
      const totalQty = getTotalQtyOfProduct(pickedItems);
      const maxSelection = Number((p as any).MaxSelection ?? 0);

      if (totalQty <= 0) {
        return {
          ok: false,
          message: t('Vui lòng chọn ít nhất 01 gói cho mỗi sản phẩm.'),
          focusProductId: String(p.Id),
        };
      }

      if (maxSelection > 0 && totalQty > maxSelection) {
        return {
          ok: false,
          message: t('Mỗi sản phẩm chỉ được chọn tối đa {{max}} lượt.', {
            max: maxSelection,
          }),
          focusProductId: String(p.Id),
        };
      }
    }

    return { ok: true };
  };

  const selectionState = useMemo(() => validateSelection(), [products, picksByProduct, t]);

  const canContinue = !loadingPackages && !error && selectionState.ok;

  const handleSubmit = () => {
    setError('');

    const v = validateSelection();
    if (!v.ok) {
      setError(v.message || t('Dữ liệu lựa chọn chưa hợp lệ.'));
      if (v.focusProductId) setActiveKeys([String(v.focusProductId)]);
      return;
    }

    const selectedOnly: PicksByProduct = {};

    Object.entries(picksByProduct).forEach(([productId, packages]) => {
      selectedOnly[productId] = (packages || []).filter((x) => Number(x?.Quantity || 0) > 0);
    });

    dispatch(setSelectedPackagesByProduct(selectedOnly));
    navigate('../persons');
  };

  if (isLoading) return <Loading />;
  if (!voucher) return null;

  return (
    <div className="OutSide">
      <Auth.FormWrapper>
        <BaseForm layout="vertical" onFinish={handleSubmit} requiredMark="optional">
          <Auth.FormTitle className="title">{t('Lựa chọn gói sử dụng')}*</Auth.FormTitle>

          {loadingPackages ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
              <Spin />
            </div>
          ) : error ? (
            <Alert type="error" showIcon message={error} />
          ) : (
            <>
              <Collapse
                accordion
                activeKey={activeKeys}
                onChange={(k) => {
                  if (!k) {
                    setActiveKeys([]);
                    return;
                  }
                  setActiveKeys(Array.isArray(k) ? k.map(String) : [String(k)]);
                }}
              >
                {products.map((p) => {
                  const productId = String(p.Id);
                  const pickedItems = picksByProduct[productId] || [];
                  const maxSelection = Number((p as any).MaxSelection ?? 0);
                  const totalQty = getTotalQtyOfProduct(pickedItems);
                  const packages = pickedItems;

                  return (
                    <Panel
                      key={productId}
                      header={
                        <PanelHeader>
                          <PanelTitle>{normStr((p as any).Name)}</PanelTitle>
                          <HeaderRight>
                            <HeaderCount $isReached={maxSelection > 0 && totalQty >= maxSelection}>
                              {totalQty}/{maxSelection}
                            </HeaderCount>
                          </HeaderRight>
                        </PanelHeader>
                      }
                    >
                      <SectionNote>
                        {t('Quý khách chọn tối đa {{max}} lượt', {
                          max: maxSelection,
                        })}
                      </SectionNote>

                      <div>
                        {packages.map((pkg: SelectedPackageItem) => {
                          const qty = Number(pkg.Quantity || 0);
                          const disablePlus = maxSelection > 0 && totalQty >= maxSelection;
                          const disableMinus = qty <= 0;

                          return (
                            <PackageRow key={String(pkg.Id)}>
                              <PackageNameWrap>
                                <PackageName>{normStr(pkg?.Name)}</PackageName>
                                {pkg?.Note ? <PackageNote>{normStr(pkg.Note)}</PackageNote> : null}
                              </PackageNameWrap>

                              <PackageActionWrap>
                                <QtyControl>
                                  <QtyButton
                                    type="button"
                                    onClick={() => handleDecrease(productId, String(pkg.Id))}
                                    disabled={disableMinus}
                                  >
                                    <MinusOutlined />
                                  </QtyButton>

                                  <QtyValue>{qty}</QtyValue>

                                  <QtyButton
                                    type="button"
                                    onClick={() => handleIncrease(productId, String(pkg.Id))}
                                    disabled={disablePlus}
                                  >
                                    <PlusOutlined />
                                  </QtyButton>
                                </QtyControl>

                                <Popover
                                  trigger={isMobile ? 'click' : 'hover'}
                                  placement={isMobile ? 'topRight' : 'leftTop'}
                                  overlayStyle={{ maxWidth: isMobile ? 'calc(100vw - 32px)' : 420 }}
                                  content={
                                    <PopoverContent>
                                      <PopoverTitle>{normStr(pkg?.Name)}</PopoverTitle>
                                      <PopoverDesc>{normStr(pkg?.Description as string)}</PopoverDesc>
                                    </PopoverContent>
                                  }
                                >
                                  <InfoButton type="button" disabled={!pkg?.Description}>
                                    <InfoCircleOutlined />
                                  </InfoButton>
                                </Popover>
                              </PackageActionWrap>
                            </PackageRow>
                          );
                        })}
                      </div>
                    </Panel>
                  );
                })}
              </Collapse>

              {!selectionState.ok && selectionState.message ? (
                <div style={{ color: '#ff4d4f', marginTop: 10 }}>{selectionState.message}</div>
              ) : null}

              <Auth.SubmitButton type="primary" htmlType="submit" style={{ marginTop: 12 }} disabled={!canContinue}>
                {t('Tiếp tục')}
              </Auth.SubmitButton>
            </>
          )}
        </BaseForm>
      </Auth.FormWrapper>
    </div>
  );
};

export default PackageForm;
